import React from 'react';
import { Palette, Code, Smartphone, PenTool, TrendingUp } from 'lucide-react';
import CardSwap, { Card } from '../ui/CardSwap';

const services = [
  {
    title: "Web Development",
    description: "Build stunning, high-performance websites that convert visitors into customers. Our AI-powered development process ensures fast, responsive, and SEO-optimized websites that drive results.",
    icon: <Code size={32} />, color: "#CCFF00"
  },
  {
    title: "Brand Identity",
    description: "Create a memorable brand that stands out in today's competitive market. From logos to color schemes, we help you establish a cohesive brand identity that resonates with your target audience.",
    icon: <Palette size={32} />, color: "#FF1744"
  },
  {
    title: "Content Strategy",
    description: "Develop compelling content that engages your audience and drives conversions. Our strategic approach ensures your message reaches the right people at the right time with maximum impact.",
    icon: <PenTool size={32} />, color: "#3EB489"
  },
  {
    title: "Social Media Marketing",
    description: "Craft a compelling story designed to enthral your audience. We understand the science and art behind viral campaigns. With our finger on the pulse of the latest trends, we create strategies that resonate and build connections that last.",
    icon: <Smartphone size={32} />, color: "#FF6B35"
  },
  {
    title: "Digital Advertising",
    description: "Maximize your ROI with targeted digital advertising campaigns. We leverage data-driven insights to create campaigns that reach your ideal customers and drive measurable results.",
    icon: <TrendingUp size={32} />, color: "#00E5FF"
  }
];

type ServicesProps = {
  onNavigate?: (page: 'home' | 'services' | 'teams' | 'contact', anchor?: string) => void;
};

const Services: React.FC<ServicesProps> = ({ onNavigate }) => {
  return (
    <section id="services" className="relative w-full bg-black py-16 sm:py-32 min-h-[700px] h-auto sm:h-[700px] flex items-center justify-center overflow-hidden">
      <div className="max-w-7xl w-full mx-auto flex flex-col md:flex-row items-center justify-center gap-8 sm:gap-12 px-4 sm:px-4 h-full">
        {/* Left Side Text Block */}
        <div className="flex-1 mb-8 sm:mb-12 md:mb-0 md:mr-8 text-center sm:text-left">
          <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-medium mb-4">
            What we do
          </div>
          <h2 className="text-3xl sm:text-5xl md:text-7xl font-black text-white mb-4 sm:mb-6 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Services
          </h2>
          <p className="text-base sm:text-xl text-gray-200 max-w-xl font-light mb-6 sm:mb-8 px-2 sm:px-0 leading-relaxed" style={{ fontFamily: 'Crimson Text, serif' }}>
            At Zeoqo, we blend creativity, technology, and strategy to deliver digital solutions that elevate your brand. Our team is passionate about building experiences that are not just beautiful, but also drive real business results. Explore what we can do for you.
          </p>
          <ul className="text-sm sm:text-lg text-gray-300 space-y-2 pl-4 sm:pl-4 list-disc text-left">
            <li>Custom web development & design</li>
            <li>Brand identity & content strategy</li>
            <li>Digital marketing & advertising</li>
            <li>AI-powered solutions for modern businesses</li>
          </ul>
        </div>
        {/* Right Side Card Carousel */}
        <div className="flex-1 min-w-[280px] sm:min-w-[340px] max-w-[500px] md:ml-0 md:mr-auto mx-auto self-center h-full flex items-center justify-center mt-4 sm:mt-8 md:mt-16" style={{ position: 'relative' }}>
          <CardSwap cardDistance={60} verticalDistance={70} delay={2000} pauseOnHover={false} height={500} width={420}>
            {services.map((service, idx) => (
              <Card key={service.title} customClass="w-full h-full flex flex-col justify-center items-start p-10 gap-6 shadow-2xl border border-white/20 bg-black/80 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl shadow-lg" style={{ backgroundColor: service.color }}>
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-2">{service.title}</h3>
                    <div className="w-12 h-1 rounded-full" style={{ backgroundColor: service.color }}></div>
                  </div>
                </div>
                <p className="text-white leading-relaxed text-lg mb-8 max-w-2xl opacity-90">{service.description}</p>
                <button
                  className="group flex items-center gap-2 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg border-2"
                  style={{ borderColor: service.color, backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
                  onClick={() => {
                    if (onNavigate) {
                      onNavigate('services');
                    } else {
                      window.location.pathname = '/services';
                    }
                  }}
                >
                  Learn More
                </button>
              </Card>
            ))}
          </CardSwap>
        </div>
      </div>
    </section>
  );
};

export default Services; 